"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAddressByPersonKey = exports.getAllPeopleAddress = exports.parseFullAddress = exports.getReportPricingTypeForSelfOrPartner = exports.getReportPricingTypeForChild = exports.isChildInPostSecondaryStudiesAgeRange = exports.isChildEligibleForTaxReport = exports.normalizeDateOfBirthString = exports.REPORT_PRICING_CHILD_ALWAYS_ADULT_AGE = exports.REPORT_PRICING_ADULT_AGE = exports.MIN_AGE_FOR_TAX_REPORT_OPTION = void 0;
const time_1 = require("../time");
const constants_1 = require("./constants");
const _1 = require(".");
exports.MIN_AGE_FOR_TAX_REPORT_OPTION = 14;
/** At tax year end: under this age = child (minor) report price for self/partner; at or over = adult. */
exports.REPORT_PRICING_ADULT_AGE = 18;
/** At tax year end: child person at or over this age always gets adult pricing (18–25 student rules do not apply). */
exports.REPORT_PRICING_CHILD_ALWAYS_ADULT_AGE = 26;
/**
 * Normalizes date-of-birth from form values (YYYY-MM-DD), ISO strings, or Date to YYYY-MM-DD.
 * Strapi/GraphQL often return `date` fields as full ISO strings; strict YYYY-MM-DD-only checks
 * would otherwise treat them as invalid.
 */
function normalizeDateOfBirthString(dateOfBirth) {
    if (dateOfBirth == null || dateOfBirth === '')
        return null;
    const s = dateOfBirth instanceof Date ? dateOfBirth.toISOString() : String(dateOfBirth).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(s))
        return s;
    const prefix = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return prefix ? prefix[1] : null;
}
exports.normalizeDateOfBirthString = normalizeDateOfBirthString;
function isChildEligibleForTaxReport(dateOfBirth, reportYear) {
    const normalized = normalizeDateOfBirthString(dateOfBirth);
    if (!normalized)
        return false;
    return ((0, time_1.hasAge)({
        dateOfBirth: normalized,
        yearsOld: exports.MIN_AGE_FOR_TAX_REPORT_OPTION,
        atGivenDate: `${reportYear}-12-31`,
    }) === true);
}
exports.isChildEligibleForTaxReport = isChildEligibleForTaxReport;
/** Ages 18–25 at end of tax year: dependent pricing depends on full-time post-secondary studies. */
function isChildInPostSecondaryStudiesAgeRange(dateOfBirth, reportYear) {
    const normalized = normalizeDateOfBirthString(dateOfBirth);
    if (!normalized)
        return false;
    const atYearEnd = `${reportYear}-12-31`;
    const isAtLeast18 = (0, time_1.hasAge)({
        dateOfBirth: normalized,
        yearsOld: exports.REPORT_PRICING_ADULT_AGE,
        atGivenDate: atYearEnd,
    });
    const isAtLeast26 = (0, time_1.hasAge)({
        dateOfBirth: normalized,
        yearsOld: exports.REPORT_PRICING_CHILD_ALWAYS_ADULT_AGE,
        atGivenDate: atYearEnd,
    });
    return isAtLeast18 === true && isAtLeast26 !== true;
}
exports.isChildInPostSecondaryStudiesAgeRange = isChildInPostSecondaryStudiesAgeRange;
/**
 * Per-person report price category for a child: minor rate under 18; 26+ adult; ages 18–25
 * dependent (child) rate by default (including when full-time post-secondary is unknown);
 * adult rate only when full-time post-secondary is explicitly set to false.
 */
function getReportPricingTypeForChild(dateOfBirth, isFullTimePostSecondaryStudent, reportYear) {
    const normalized = normalizeDateOfBirthString(dateOfBirth);
    if (!normalized)
        return constants_1.PERSON_TYPES.partner;
    const atYearEnd = `${reportYear}-12-31`;
    if ((0, time_1.hasAge)({
        dateOfBirth: normalized,
        yearsOld: exports.REPORT_PRICING_ADULT_AGE,
        atGivenDate: atYearEnd,
    }) !== true) {
        return constants_1.PERSON_TYPES.child;
    }
    if ((0, time_1.hasAge)({
        dateOfBirth: normalized,
        yearsOld: exports.REPORT_PRICING_CHILD_ALWAYS_ADULT_AGE,
        atGivenDate: atYearEnd,
    }) === true) {
        return constants_1.PERSON_TYPES.partner;
    }
    if (isFullTimePostSecondaryStudent === false) {
        return constants_1.PERSON_TYPES.partner;
    }
    return constants_1.PERSON_TYPES.child;
}
exports.getReportPricingTypeForChild = getReportPricingTypeForChild;
/**
 * Report price category for {@link PERSON_TYPES.self} or {@link PERSON_TYPES.partner}: the
 * reduced (child) rate applies only when the person is under 18 at the tax year end. Ages 18+ always
 * use the adult rate. Full-time post-secondary is not considered here (that applies to
 * {@link getReportPricingTypeForChild} only).
 */
function getReportPricingTypeForSelfOrPartner(dateOfBirth, reportYear) {
    const normalized = normalizeDateOfBirthString(dateOfBirth);
    if (!normalized)
        return constants_1.PERSON_TYPES.partner;
    const atYearEnd = `${reportYear}-12-31`;
    if ((0, time_1.hasAge)({
        dateOfBirth: normalized,
        yearsOld: exports.REPORT_PRICING_ADULT_AGE,
        atGivenDate: atYearEnd,
    }) !== true) {
        return constants_1.PERSON_TYPES.child;
    }
    return constants_1.PERSON_TYPES.partner;
}
exports.getReportPricingTypeForSelfOrPartner = getReportPricingTypeForSelfOrPartner;
// List of apartment-related keywords to remove from address2
const APARTMENT_KEYWORDS = [
    'apt',
    'apartment',
    'unit',
    '#',
    'suite',
    'bldg',
    'building',
    'app',
    'appartement',
    'unité',
    'bureau',
    'local',
];
/**
 * @example
 *
 * console.log(parseFullAddress('100-1234 Rue de la Paix', ''))
 * // { doorNumber: '1234', street: 'Rue de la Paix', apartmentNumber: '100' }
 *
 * console.log(parseFullAddress('456 Boulevard Saint-Laurent', '#302'))
 * // { doorNumber: '456', street: 'Boulevard Saint-Laurent', apartmentNumber: '302' }
 *
 * console.log(parseFullAddress('1010 Avenue des Champs-Élysées', 'Appartement 7B'))
 * // { doorNumber: '1010', street: 'Avenue des Champs-Élysées', apartmentNumber: '7B' }
 *
 * console.log(parseFullAddress('789 Rue du Faubourg', ''))
 * // { doorNumber: '789', street: 'Rue du Faubourg', apartmentNumber: '' }
 *
 * console.log(parseFullAddress("Avenue de l'Opéra", 'Bureau 205'))
 * // { doorNumber: '', street: "Avenue de l'Opéra", apartmentNumber: '205' }
 *
 * console.log(parseFullAddress('10', 'Rue Amanda-Olive #502'))
 * // { doorNumber: '10', street: 'Rue Amanda-Olive', apartmentNumber: '502' }
 */
function parseFullAddress(address1, address2) {
    let doorNumber = '';
    let street = '';
    let apartment = '';
    let addressLine1 = address1;
    let addressLine2 = address2;
    const apartmentRegex = new RegExp(`(${APARTMENT_KEYWORDS.join('|')})([^A-Za-z])`, 'gi');
    // If address1 has only numbers, assume the street name is in address2
    if (/^\d+$/.test(address1.trim())) {
        const concatenated = [address1, address2].map(a => (a || '').trim()).join(' ');
        const separator = '@@#_#@@';
        // Still split any possible apartment number
        const [line1, line2] = concatenated.replace(apartmentRegex, `${separator}$2`).split(separator);
        addressLine1 = line1;
        addressLine2 = line2;
    }
    // Check if address1 starts with "100-1234" format (apartment-door format)
    const aptDoorMatch = addressLine1.match(/^(\d+)-(\d+)\s+/);
    if (aptDoorMatch) {
        apartment = aptDoorMatch[1]; // Extract apartment number
        doorNumber = aptDoorMatch[2]; // Extract door number
        street = addressLine1.slice(aptDoorMatch[0].length).trim(); // Remove extracted part
    }
    else {
        // Standard format (door number at the start)
        const doorMatch = addressLine1.match(/^(\d+)\s+/);
        if (doorMatch) {
            doorNumber = doorMatch[1];
            street = addressLine1.slice(doorMatch[0].length).trim();
        }
        else {
            street = addressLine1.trim(); // If no door number, assume entire input is street
        }
    }
    // Clean up address2 by removing apartment keywords and leading #
    if (addressLine2) {
        apartment = addressLine2
            .replace(apartmentRegex, '$2')
            .replace(/[\.,]/g, '')
            .replace(/\b[A-Za-z]{2,}\b/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();
    }
    return { doorNumber, street, apartment };
}
exports.parseFullAddress = parseFullAddress;
function getAllPeopleAddress(personEntries) {
    const addresses = {};
    Object.entries(personEntries).forEach(([personKey, person]) => {
        const personKeyWithAddress = person.personType !== constants_1.PERSON_TYPES.self && (person?.hasSameAddress || !person?.province)
            ? (0, _1.getPersonKey)(constants_1.PERSON_TYPES.self)
            : personKey;
        const personWithAddress = personEntries[personKeyWithAddress];
        addresses[personKey] = {
            address1: personWithAddress?.address1 || '',
            address2: personWithAddress?.address2 || '',
            city: personWithAddress?.city || '',
            province: personWithAddress?.province || '',
            postalCode: personWithAddress?.postalCode || '',
        };
    });
    return addresses;
}
exports.getAllPeopleAddress = getAllPeopleAddress;
function getAddressByPersonKey(personEntries, personKey) {
    const address = getAllPeopleAddress(personEntries)[personKey];
    return address;
}
exports.getAddressByPersonKey = getAddressByPersonKey;
//# sourceMappingURL=person.js.map